import { useEffect, useState } from 'react';
import { Country } from '@tiendanube/common/src/enums';
import { FS_NUVEMPAGO_STORE_CREATION_VARIANT } from 'App/featuresFlags';
import useHasTags from 'App/hooks/useHasTags/useHasTags';
import { STORE_REGISTER_LINK } from 'commons/constants/externalLinks';
import { getTiendanubeUrl } from 'commons/utils/getTiendanubeUrl';
import { useGetBusinessPhone } from 'domains/Account/hooks';
import { useIsLogged, useIsMobileDevice } from 'domains/Auth/hooks';
import useGetCountry from 'domains/Auth/hooks/useGetCountry/useGetCountry';
import useGetInvoiceInfo from 'domains/Billing/Invoices/hooks/useGetInvoiceInfo';
import { useGetFeatureFlagVariantQuery } from 'domains/NuvemPagoCommons/nuvemPagoCommonsApi';

const getStoreRegisterUrl = (country: Country) =>
  getTiendanubeUrl(country) + STORE_REGISTER_LINK[country];

const BR_ONBOARDING_TAGS = [
  'br-onboarding-variant-1',
  'br-onboarding-variant-2',
];

export function useRequiredDataValidation() {
  const [isRequiredDataMissing, setIsRequiredDataMissing] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  const [hasTriggeredDataLoad, setHasTriggeredDataLoad] = useState(false);

  const { isLogin } = useIsLogged();
  const isMobile = useIsMobileDevice();
  const country = useGetCountry();
  const {
    invoiceInfo,
    fetchInvoiceInfo,
    isLoading: isLoadingInvoice,
    isError: isErrorToFetchInvoice,
  } = useGetInvoiceInfo();
  const document = invoiceInfo?.numberId;
  const {
    phoneNumber,
    fetchBusinessPhone,
    isLoading: isLoadingPhone,
    isError: isErrorToFetchPhone,
  } = useGetBusinessPhone();

  const hasBrOnboardingTag = useHasTags(BR_ONBOARDING_TAGS).some(
    (hasTag) => hasTag,
  );

  const shouldValidate = isLogin && !isMobile && country === Country.BR;

  const { data: featureFlagVariant, isLoading: isLoadingFeatureFlag } =
    useGetFeatureFlagVariantQuery(FS_NUVEMPAGO_STORE_CREATION_VARIANT, {
      skip: !shouldValidate,
    });

  useEffect(() => {
    if (
      !hasTriggeredDataLoad &&
      shouldValidate &&
      hasBrOnboardingTag &&
      featureFlagVariant?.enabled
    ) {
      fetchInvoiceInfo();
      fetchBusinessPhone();
      setRedirectUrl(getStoreRegisterUrl(country));
      setHasTriggeredDataLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, featureFlagVariant, hasTriggeredDataLoad]);

  useEffect(() => {
    const isLoadingData =
      isLoadingFeatureFlag || isLoadingPhone || isLoadingInvoice;
    const isErrorData = isErrorToFetchInvoice || isErrorToFetchPhone;
    const isRequiredDataVariant = ['1.0', '2.0'].includes(
      featureFlagVariant?.name || '',
    );
    const isMissingData = !phoneNumber || !document;

    if (
      isLogin &&
      !isMobile &&
      country === Country.BR &&
      hasTriggeredDataLoad &&
      !isLoadingData &&
      !isErrorData &&
      featureFlagVariant?.enabled &&
      isRequiredDataVariant &&
      isMissingData &&
      hasBrOnboardingTag
    ) {
      setIsRequiredDataMissing(true);
    }
  }, [
    isLogin,
    isMobile,
    featureFlagVariant,
    phoneNumber,
    document,
    invoiceInfo,
    isLoadingPhone,
    isLoadingInvoice,
    isLoadingFeatureFlag,
    isErrorToFetchInvoice,
    isErrorToFetchPhone,
    country,
    hasBrOnboardingTag,
    hasTriggeredDataLoad,
  ]);

  return {
    isRequiredDataMissing,
    testGroup: featureFlagVariant?.name,
    redirectUrl,
  };
}
